// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.
/**
 * Daily notes, templates, note names, and vault settings: shared by the web, desktop, and
 * mobile apps so they create the same files (P3-T09, P5-T05).
 */
import { basename, extname, fileKind } from "./path.js";
export const VAULT_SETTINGS_PATH = ".granite/settings.json";
export const defaultVaultSettings = {
    dailyFolder: "Daily notes",
    templatesFolder: "Templates",
    attachmentsFolder: "Attachments",
};
/** Reads vault settings, keeping defaults for anything missing or malformed. */
export function parseVaultSettings(text) {
    if (!text)
        return defaultVaultSettings;
    try {
        const v = JSON.parse(text);
        const folder = (x, d) => typeof x === "string" ? x.trim().replace(/^\/+|\/+$/g, "").replace(/\/+/g, "/") : d;
        return {
            dailyFolder: folder(v.dailyFolder, defaultVaultSettings.dailyFolder),
            templatesFolder: folder(v.templatesFolder, defaultVaultSettings.templatesFolder),
            attachmentsFolder: folder(v.attachmentsFolder, defaultVaultSettings.attachmentsFolder),
        };
    }
    catch {
        return defaultVaultSettings;
    }
}
const inFolder = (folder, name) => (folder ? `${folder}/${name}` : name);
function pad(n) {
    return String(n).padStart(2, "0");
}
/** YYYY-MM-DD in local time. */
export function isoDate(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
export function dailyNotePath(d, folder = defaultVaultSettings.dailyFolder) {
    return inFolder(folder, `${isoDate(d)}.md`);
}
export function dailyTemplatePath(templatesFolder = defaultVaultSettings.templatesFolder) {
    return inFolder(templatesFolder, "Daily.md");
}
/** A note's title: its file name without ".md" (or ".canvas" for a canvas); other files keep theirs. */
export function noteTitle(path) {
    const name = basename(path);
    return fileKind(path) === "markdown" || isCanvas(path) ? name.slice(0, name.length - extname(name).length) : name;
}
/** A JSON Canvas file. */
export function isCanvas(path) {
    return extname(path).toLowerCase() === ".canvas";
}
/** Files in folders starting with "." (.obsidian/, .granite/) are kept but not shown. */
export function isHidden(path) {
    return path.split("/").some((seg) => seg.startsWith("."));
}
export function isTemplate(path, folder = defaultVaultSettings.templatesFolder) {
    return (folder === "" || path.startsWith(`${folder}/`)) && fileKind(path) === "markdown" && !isHidden(path);
}
/**
 * Fills a template: {{title}}, {{date}} (YYYY-MM-DD), and {{time}} (HH:mm). Anything else in
 * braces is left as written.
 */
export function applyTemplate(template, vars) {
    const values = {
        title: vars.title,
        date: isoDate(vars.now),
        time: `${pad(vars.now.getHours())}:${pad(vars.now.getMinutes())}`,
    };
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, name) => values[name.toLowerCase()] ?? m);
}
/** Characters that cannot appear in a note name typed by the user (Appendix B path rules). */
const FORBIDDEN = /[\\/:*?"<>|#^[\]]/g;
/** The characters a typed note or folder name may not hold, as shown in the error. */
export const FORBIDDEN_NAME_CHARS = '/ \\ : * ? " < > | # ^ [ ]';
/**
 * Whether a typed note or folder name holds a refused character. "/" is one: a name never
 * makes folders ("A/B Testing" is not "B Testing" inside "A").
 */
export function hasForbiddenNameChars(name) {
    // eslint-disable-next-line no-control-regex -- control characters are refused too
    return /[\\/:*?"<>|#^[\]\u0000-\u001f\u007f]/u.test(name);
}
/** Turns a typed name into a note path in folder ("" for the root). */
export function notePathFromName(name, folder) {
    const clean = name.replace(FORBIDDEN, " ").replace(/\s+/g, " ").trim() || "Untitled";
    const file = clean.toLowerCase().endsWith(".md") ? clean : `${clean}.md`;
    return folder ? `${folder}/${file}` : file;
}
/**
 * Where a note goes when renamed to a typed name in its folder: a canvas stays a canvas
 * (its title hides ".canvas" as a note's hides ".md").
 */
export function renamedNotePath(path, name) {
    const slash = path.lastIndexOf("/");
    const folder = slash >= 0 ? path.slice(0, slash) : "";
    if (!isCanvas(path))
        return notePathFromName(name, folder);
    let clean = name.replace(FORBIDDEN, " ").replace(/\s+/g, " ").trim();
    if (clean.toLowerCase().endsWith(".canvas"))
        clean = clean.slice(0, -".canvas".length).trim();
    const file = `${clean || "Untitled"}.canvas`;
    return folder ? `${folder}/${file}` : file;
}
/**
 * Where a duplicate of a file goes: beside it, numbered the way new notes get free names
 * ("Plan.md" → "Plan 2.md", "Plan 2.md" → "Plan 3.md", "photo.png" → "photo 2.png"). A small
 * number already at the end of the name is counted on rather than repeated ("Plan 2 2.md").
 */
export function duplicatePath(path, taken) {
    const slash = path.lastIndexOf("/");
    const name = path.slice(slash + 1);
    const dot = name.lastIndexOf(".");
    const ext = dot > 0 ? name.slice(dot) : "";
    let stem = dot > 0 ? name.slice(0, dot) : name;
    let n = 2;
    const numbered = /^(.+) ([2-9]|[1-9]\d{1,2})$/u.exec(stem);
    if (numbered?.[1] && numbered[2]) {
        stem = numbered[1];
        n = Number(numbered[2]) + 1;
    }
    const folder = path.slice(0, slash + 1);
    let to = `${folder}${stem} ${n}${ext}`;
    while (taken(to))
        to = `${folder}${stem} ${++n}${ext}`;
    return to;
}
/** A free name for an attachment in the attachments folder. */
export function attachmentPath(fileName, taken, folder = defaultVaultSettings.attachmentsFolder) {
    const safe = fileName.replace(/[\\/:*?"<>|#^[\]]/g, "-").trim() || "pasted";
    const dot = safe.lastIndexOf(".");
    const stem = dot > 0 ? safe.slice(0, dot) : safe;
    const ext = dot > 0 ? safe.slice(dot) : "";
    let path = inFolder(folder, safe);
    for (let n = 2; taken(path); n++)
        path = inFolder(folder, `${stem} ${n}${ext}`);
    return path;
}
//# sourceMappingURL=notes.js.map